/**
 * FieldPropertyPanel — 커스텀 필드 속성 + 옵션 편집 모달.
 *
 * 구조: 한 모달 박스 안에 두 step 패널.
 *   Step "main":    속성명/플레이스홀더(다국어 탭) + 설정 + (SELECT) '옵션 N개 관리 →' 카드
 *   Step "options": ← 뒤로 헤더 + 옵션 추가/리스트 (서버에서 partial HTML fetch)
 *
 * 다국어: `locale-tabs` 컴포넌트 패턴(`data-locale-tabs`/`data-locale-target`/`data-locale-panel`)
 * 을 그대로 빌드 → 초기화 시 LocaleTabs.initAll 로 핸들러 부착. ko/en 하드코딩 없음.
 *
 * 옵션 CRUD 후: window.location.reload() 하지 않고 GET /options partial 을 fetch 해서
 *               옵션 리스트 영역만 innerHTML 교체. 모달은 열린 상태 유지.
 */
import { Toast } from "../utils/Toast";
import { Modal } from "../utils/Modal";
import Sortable from "sortablejs";
import { LocaleTabs } from "../admin/components/LocaleTabs";

type JsonResult = {
    result?: string;
    msg?: string;
    data?: Record<string, unknown>;
};

const LOCALE_LABELS: Record<string, string> = {
    ko: "한국어",
    en: "English",
    ja: "日本語",
    zh: "中文",
};

function csrfToken(): string {
    return (
        document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
        document.querySelector<HTMLInputElement>('input[name="_token"]')?.value ??
        ""
    );
}

async function postFormData(url: string, fd: FormData, method = "POST"): Promise<JsonResult> {
    const res = await fetch(url, {
        method,
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": csrfToken(),
        },
        body: fd,
    });
    const text = await res.text();
    try {
        return JSON.parse(text) as JsonResult;
    } catch {
        return { result: "fail", msg: text || `HTTP ${res.status}` };
    }
}

async function patchJson(url: string, body: Record<string, unknown>): Promise<JsonResult> {
    const res = await fetch(url, {
        method: "PATCH",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": csrfToken(),
        },
        body: JSON.stringify(body),
    });
    const text = await res.text();
    try {
        return JSON.parse(text) as JsonResult;
    } catch {
        return { result: "fail", msg: text || `HTTP ${res.status}` };
    }
}

// -----------------------------------------------------------------------------
// 모달 라이프사이클
// -----------------------------------------------------------------------------

let activeModal: HTMLElement | null = null;
let escListener: ((ev: KeyboardEvent) => void) | null = null;

function closeActiveModal(): void {
    if (activeModal) {
        activeModal.remove();
        activeModal = null;
    }
    if (escListener) {
        document.removeEventListener("keydown", escListener);
        escListener = null;
    }
}

// -----------------------------------------------------------------------------
// 빌더 헬퍼
// -----------------------------------------------------------------------------

function buildToggleRow(label: string, name: string, checked: boolean): HTMLElement {
    const row = document.createElement("div");
    row.className = "fp-toggle-row";
    const lbl = document.createElement("label");
    lbl.className = "fp-toggle-row__label";
    lbl.textContent = label;
    const switchWrap = document.createElement("label");
    switchWrap.className = "fp-toggle-switch";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.name = name;
    input.value = "1";
    input.checked = checked;
    input.className = "fp-toggle-switch__input";
    const slider = document.createElement("span");
    slider.className = "fp-toggle-switch__slider";
    switchWrap.append(input, slider);
    row.append(lbl, switchWrap);
    return row;
}

function buildInputRow(label: string, name: string, value: string): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "fp-input-row";
    const lbl = document.createElement("label");
    lbl.className = "fp-input-row__label";
    lbl.textContent = label;
    const input = document.createElement("input");
    input.type = "text";
    input.name = name;
    input.value = value;
    input.className = "fp-input-row__input";
    wrap.append(lbl, input);
    return wrap;
}

function buildSection(title: string): { section: HTMLElement; appendInto: HTMLElement } {
    const section = document.createElement("div");
    section.className = "admin-modal__section";
    if (title !== "") {
        const t = document.createElement("div");
        t.className = "admin-modal__section-title";
        t.textContent = title;
        section.appendChild(t);
    }
    return { section, appendInto: section };
}

function buildTypeSelector(typeOptions: TypeOption[], currentType: string): HTMLElement {
    const grid = document.createElement("div");
    grid.className = "fp-type-grid";
    typeOptions.forEach((opt) => {
        const label = document.createElement("label");
        label.className = "fp-type-card" + (opt.value === currentType ? " fp-type-card--selected" : "");
        const radio = document.createElement("input");
        radio.type = "radio";
        radio.name = "type";
        radio.value = opt.value;
        radio.checked = opt.value === currentType;
        radio.className = "fp-type-card__radio";
        radio.addEventListener("change", () => {
            grid.querySelectorAll(".fp-type-card").forEach((c) => c.classList.remove("fp-type-card--selected"));
            label.classList.add("fp-type-card--selected");
        });
        const icon = document.createElement("span");
        icon.className = "fp-type-card__icon";
        icon.innerHTML = `<i class="${opt.icon}" aria-hidden="true"></i>`;
        const text = document.createElement("span");
        text.className = "fp-type-card__text";
        const lbl = document.createElement("span");
        lbl.className = "fp-type-card__label";
        lbl.textContent = opt.label;
        const desc = document.createElement("span");
        desc.className = "fp-type-card__desc";
        desc.textContent = opt.desc;
        text.append(lbl, desc);
        label.append(radio, icon, text);
        grid.appendChild(label);
    });
    return grid;
}

/**
 * locale-tabs.php 의 마크업을 TS 로 빌드.
 * `LocaleTabs.initAll(modal)` 로 초기화 → 탭 클릭 시 panel 토글.
 */
function buildLocaleTabs(
    locales: string[],
    idPrefix: string,
    renderPanel: (locale: string) => HTMLElement,
): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "admin-locale-tabs";
    wrap.setAttribute("data-locale-tabs", "");

    const bar = document.createElement("div");
    bar.className = "admin-locale-tabs__bar";
    bar.setAttribute("role", "tablist");
    bar.setAttribute("aria-label", "언어 선택");

    const panels = document.createElement("div");
    panels.className = "admin-locale-tabs__panels";

    const active = locales[0] ?? "ko";
    locales.forEach((loc) => {
        const isActive = loc === active;
        const tabId = `${idPrefix}-tab-${loc}`;
        const panelId = `${idPrefix}-panel-${loc}`;

        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "admin-locale-tabs__tab" + (isActive ? " admin-locale-tabs__tab--active" : "");
        tab.setAttribute("role", "tab");
        tab.setAttribute("data-locale-target", loc);
        tab.id = tabId;
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
        tab.setAttribute("aria-controls", panelId);

        const lblSpan = document.createElement("span");
        lblSpan.className = "admin-locale-tabs__tab-label";
        lblSpan.textContent = LOCALE_LABELS[loc] ?? loc.toUpperCase();
        const codeSpan = document.createElement("span");
        codeSpan.className = "admin-locale-tabs__tab-code";
        codeSpan.textContent = loc.toUpperCase();
        tab.append(lblSpan, codeSpan);
        bar.appendChild(tab);

        const panel = document.createElement("div");
        panel.className = "admin-locale-tabs__panel" + (isActive ? "" : " admin-locale-tabs__panel--hidden");
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("data-locale-panel", loc);
        panel.id = panelId;
        panel.setAttribute("aria-labelledby", tabId);
        panel.tabIndex = 0;
        panel.appendChild(renderPanel(loc));
        panels.appendChild(panel);
    });

    wrap.append(bar, panels);
    return wrap;
}

// -----------------------------------------------------------------------------
// Props 파싱
// -----------------------------------------------------------------------------

type LocaleEntry = { label: string; placeholder: string };

interface TypeOption {
    value: string;
    label: string;
    icon: string;
    desc: string;
}

interface FieldProps {
    fieldName: string;
    fieldLabelKo: string;
    updateUrl: string;
    locales: string[];
    translations: Record<string, LocaleEntry>;
    isRequired: boolean;
    isVisible: boolean;
    isMultiple: boolean;
    isFilterable: boolean;
    isPublicSearchable: boolean;
    isPublicFilter: boolean;
    isPublicSortable: boolean;
    isSelect: boolean;
    fieldType: string;
    typeOptions: TypeOption[];
    optionCount: number;
    optionStoreUrl: string;
    optionReorderUrl: string;
    optionListUrl: string;
}

function parseProps(card: HTMLElement): FieldProps {
    const d = card.dataset;
    const raw: Record<string, unknown> = d.fieldProps ? JSON.parse(d.fieldProps) : {};
    const locales = Array.isArray(raw.locales) && raw.locales.length > 0
        ? (raw.locales as string[])
        : ["ko", "en"];

    const translations: Record<string, LocaleEntry> = {};
    const rawTr = (raw.translations ?? {}) as Record<string, Partial<LocaleEntry>>;
    locales.forEach((loc) => {
        translations[loc] = {
            label:       String(rawTr[loc]?.label ?? ""),
            placeholder: String(rawTr[loc]?.placeholder ?? ""),
        };
    });

    return {
        fieldName:        String(raw.fieldName ?? ""),
        fieldLabelKo:     String(raw.fieldLabelKo ?? ""),
        updateUrl:        d.updateUrl ?? "",
        locales,
        translations,
        isRequired:       raw.isRequired === true,
        isVisible:        raw.isVisible !== false,
        isMultiple:       raw.isMultiple === true,
        isFilterable:     raw.isFilterable === true,
        isPublicSearchable: raw.isPublicSearchable === true,
        isPublicFilter:     raw.isPublicFilter === true,
        isPublicSortable:   raw.isPublicSortable === true,
        isSelect:         raw.isSelect === true,
        fieldType:        String(raw.fieldType ?? ""),
        typeOptions:      Array.isArray(raw.typeOptions) ? (raw.typeOptions as TypeOption[]) : [],
        optionCount:      Number(raw.optionCount ?? 0),
        optionStoreUrl:   String(raw.optionStoreUrl ?? d.optionStoreUrl ?? ""),
        optionReorderUrl: String(raw.optionReorderUrl ?? d.optionReorderUrl ?? ""),
        optionListUrl:    String(raw.optionListUrl ?? d.optionListUrl ?? ""),
    };
}

// -----------------------------------------------------------------------------
// 옵션 step 부분 갱신
// -----------------------------------------------------------------------------

interface OptionStepRefs {
    root: HTMLElement;            // 옵션 리스트가 들어가는 컨테이너
    countLabel: HTMLElement;      // 메인 step 의 'N개 관리' 라벨
    state: { count: number };
}

function bindOptionListSortable(listRoot: HTMLElement, optionReorderUrl: string): void {
    const ul = listRoot.querySelector<HTMLElement>("[data-option-list]");
    if (!ul || optionReorderUrl === "") return;
    Sortable.create(ul, {
        animation: 150,
        handle: ".admin-sortable__handle",
        ghostClass: "admin-sortable__ghost",
        draggable: "[data-option-card]",
        onEnd: async () => {
            const items = Array.from(ul.querySelectorAll<HTMLElement>("[data-option-card]"))
                .map((el, idx) => ({ id: Number(el.dataset.optionId ?? "0"), sort_order: (idx + 1) * 10 }));
            const json = await patchJson(optionReorderUrl, { items });
            if (json.result === "success") {
                Toast.success(json.msg ?? "옵션 순서를 저장했습니다.");
            } else {
                Toast.error(json.msg ?? "순서 저장 실패");
            }
        },
    });
}

async function refreshOptionList(refs: OptionStepRefs, listUrl: string, optionReorderUrl: string): Promise<void> {
    if (listUrl === "") return;
    try {
        const res = await fetch(listUrl, {
            method: "GET",
            headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        });
        if (!res.ok) {
            Toast.error("옵션 목록을 불러오지 못했습니다.");
            return;
        }
        refs.root.innerHTML = await res.text();
        bindOptionListSortable(refs.root, optionReorderUrl);
        LocaleTabs.initAll(refs.root);

        // 카운트 갱신
        const count = refs.root.querySelectorAll("[data-option-card]").length;
        refs.state.count = count;
        refs.countLabel.textContent = `${count}개 관리`;
    } catch {
        Toast.error("옵션 목록 새로고침 실패");
    }
}

async function destroyOption(card: HTMLElement | null): Promise<boolean> {
    if (!card) return false;
    if (!(await Modal.confirm("이 옵션을 삭제할까요? 이 옵션을 선택한 기존 게시글의 값은 비워집니다."))) return false;
    const url = card.dataset.destroyUrl ?? "";
    if (url === "") return false;
    const res = await fetch(url, {
        method: "DELETE",
        headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            "X-CSRF-TOKEN": csrfToken(),
        },
        body: JSON.stringify({}),
    });
    const text = await res.text();
    let json: JsonResult = {};
    try { json = JSON.parse(text); } catch { json = { result: "fail", msg: text }; }
    if (json.result === "success") {
        Toast.success(json.msg ?? "옵션을 삭제했습니다.");
        return true;
    }
    Toast.error(json.msg ?? "옵션 삭제 실패");
    return false;
}

// -----------------------------------------------------------------------------
// 메인 진입점
// -----------------------------------------------------------------------------

export function openFieldPropertyPanel(_triggerBtn: HTMLElement, card: HTMLElement): void {
    if (activeModal) {
        const wasThis = activeModal.dataset.forFieldId === card.dataset.fieldId;
        closeActiveModal();
        if (wasThis) return;
    }

    const props = parseProps(card);

    // -- 모달 컨테이너 --
    const modal = document.createElement("div");
    modal.className = "admin-modal";
    modal.dataset.forFieldId = card.dataset.fieldId ?? "";

    const backdrop = document.createElement("div");
    backdrop.className = "admin-modal__backdrop";
    backdrop.tabIndex = -1;
    backdrop.addEventListener("click", closeActiveModal);
    modal.appendChild(backdrop);

    const box = document.createElement("div");
    box.className = "admin-modal__box admin-modal__box--md";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-labelledby", "fp-modal-title");
    modal.appendChild(box);

    // -- 헤더 --
    const head = document.createElement("div");
    head.className = "admin-modal__head";

    const backBtn = document.createElement("button");
    backBtn.type = "button";
    backBtn.className = "admin-modal__back admin-modal__back--hidden";
    backBtn.setAttribute("aria-label", "뒤로");
    backBtn.innerHTML = '<i class="fa-light fa-arrow-left" aria-hidden="true"></i>';

    const headTitle = document.createElement("h2");
    headTitle.id = "fp-modal-title";
    headTitle.className = "admin-modal__title";
    headTitle.innerHTML = `속성 편집<span class="admin-modal__title-sub">${props.fieldLabelKo}${props.fieldName ? ` · <code>${props.fieldName}</code>` : ""}</span>`;

    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "admin-modal__close";
    closeBtn.setAttribute("aria-label", "닫기");
    closeBtn.innerHTML = "&times;";
    closeBtn.addEventListener("click", closeActiveModal);

    head.append(backBtn, headTitle, closeBtn);
    box.appendChild(head);

    // -- 바디 (멀티스텝) --
    const body = document.createElement("div");
    body.className = "admin-modal__body admin-modal__body--form";

    const steps = document.createElement("div");
    steps.className = "admin-modal__steps";
    steps.dataset.active = "main";
    body.appendChild(steps);

    const mainStep = document.createElement("div");
    mainStep.className = "admin-modal__step admin-modal__step--main";
    mainStep.dataset.step = "main";
    steps.appendChild(mainStep);

    const optionsStep = document.createElement("div");
    optionsStep.className = "admin-modal__step admin-modal__step--options";
    optionsStep.dataset.step = "options";
    steps.appendChild(optionsStep);

    // -- 메인 step --
    // 다국어 입력 (속성명 + 플레이스홀더) — 언어 탭 1개로 통합
    const fid = card.dataset.fieldId ?? "x";
    const localeSec = buildSection("");
    localeSec.appendInto.appendChild(
        buildLocaleTabs(props.locales, `fp-locale-${fid}`, (loc) => {
            const frag = document.createElement("div");
            frag.className = "fp-locale-fields";
            const labelV = props.translations[loc]?.label ?? "";
            const phV = props.translations[loc]?.placeholder ?? "";
            frag.appendChild(buildInputRow("속성명", `translations[${loc}][label]`, labelV));
            frag.appendChild(buildInputRow("플레이스홀더", `translations[${loc}][placeholder]`, phV));
            return frag;
        }),
    );
    mainStep.appendChild(localeSec.section);

    // 필드 타입 선택
    const typeSec = buildSection("필드 타입");
    if (props.typeOptions.length > 0) {
        typeSec.appendInto.appendChild(buildTypeSelector(props.typeOptions, props.fieldType));
    }
    mainStep.appendChild(typeSec.section);

    // 설정 + 옵션 진입 카드 — 타입 변경 시 동적으로 갱신되는 영역
    const dynamicSec = document.createElement("div");
    dynamicSec.className = "fp-dynamic-sec";
    mainStep.appendChild(dynamicSec);

    // SELECT 계열 여부 판단
    function isSelectType(t: string): boolean {
        return t === "select" || t === "select_multi";
    }

    // SELECT 전용 설정(다중 선택/목록 필터) + 옵션 진입 카드를 dynamicSec에 렌더
    let optionCountLabel: HTMLElement | null = null;
    function renderDynamicSec(currentType: string): void {
        dynamicSec.innerHTML = "";
        optionCountLabel = null;

        const configSec = buildSection("설정");
        configSec.appendInto.appendChild(buildToggleRow("필수 입력", "is_required", props.isRequired));
        configSec.appendInto.appendChild(buildToggleRow("목록 표시", "is_visible", props.isVisible));
        if (isSelectType(currentType)) {
            configSec.appendInto.appendChild(buildToggleRow("다중 선택", "is_multiple", currentType === "select_multi" || props.isMultiple));
            configSec.appendInto.appendChild(buildToggleRow("목록 필터", "is_filterable", props.isFilterable));
        }
        dynamicSec.appendChild(configSec.section);

        // 공개(홈페이지) 노출 토글 — 타입별로 의미 있는 것만 노출.
        //  검색=text/longtext, 필터=select 류, 정렬=date/number.
        const canSearch = currentType === "text" || currentType === "longtext";
        const canFilter = isSelectType(currentType);
        const canSort   = currentType === "date" || currentType === "number";
        if (canSearch || canFilter || canSort) {
            const pubSec = buildSection("홈페이지 노출");
            if (canSearch) {
                pubSec.appendInto.appendChild(buildToggleRow("검색 대상", "is_public_searchable", props.isPublicSearchable));
            }
            if (canFilter) {
                pubSec.appendInto.appendChild(buildToggleRow("필터 노출", "is_public_filter", props.isPublicFilter));
            }
            if (canSort) {
                pubSec.appendInto.appendChild(buildToggleRow("정렬 노출", "is_public_sortable", props.isPublicSortable));
            }
            dynamicSec.appendChild(pubSec.section);
        }

        if (isSelectType(currentType)) {
            const optSec = buildSection("옵션");
            const enterCard = document.createElement("button");
            enterCard.type = "button";
            enterCard.className = "fp-options-card";
            enterCard.innerHTML = `
                <span class="fp-options-card__icon"><i class="fa-light fa-list" aria-hidden="true"></i></span>
                <span class="fp-options-card__text">
                    <span class="fp-options-card__label">옵션 관리</span>
                    <span class="fp-options-card__count" data-options-count>${props.optionCount}개 관리</span>
                </span>
                <span class="fp-options-card__chevron"><i class="fa-light fa-chevron-right" aria-hidden="true"></i></span>
            `;
            optSec.appendInto.appendChild(enterCard);
            dynamicSec.appendChild(optSec.section);
            optionCountLabel = enterCard.querySelector<HTMLElement>("[data-options-count]");
            enterCard.addEventListener("click", () => goToStep("options"));
        }
    }
    renderDynamicSec(props.fieldType);

    // 타입 라디오 변경 시 dynamicSec 갱신
    typeSec.section.addEventListener("change", (ev) => {
        const radio = ev.target as HTMLInputElement;
        if (radio.name === "type") {
            renderDynamicSec(radio.value);
            // optionRefs.countLabel 갱신 (옵션 카드가 새로 생성될 수 있으므로)
            optionRefs.countLabel = optionCountLabel ?? document.createElement("span");
        }
    });

    // -- 옵션 step --
    const optHeader = document.createElement("div");
    optHeader.className = "admin-modal__step-header";
    const optTitle = document.createElement("div");
    optTitle.className = "admin-modal__step-title";
    optTitle.textContent = "옵션 관리";
    const optDesc = document.createElement("p");
    optDesc.className = "admin-modal__step-desc";
    optDesc.textContent = `"${props.fieldLabelKo}" 필드의 선택지(옵션)를 추가·수정·정렬합니다.`;
    optHeader.append(optTitle, optDesc);
    optionsStep.appendChild(optHeader);

    const optionsRoot = document.createElement("div");
    optionsRoot.className = "fp-options-root";
    optionsRoot.dataset.fpOptionListRoot = "1";
    optionsStep.appendChild(optionsRoot);

    box.appendChild(body);

    // -- 푸터 --
    const footer = document.createElement("div");
    footer.className = "admin-modal__footer";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "admin-btn admin-btn--secondary admin-btn--sm";
    cancelBtn.textContent = "취소";
    cancelBtn.addEventListener("click", closeActiveModal);
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "admin-btn admin-btn--primary admin-btn--sm";
    saveBtn.textContent = "저장";
    footer.append(cancelBtn, saveBtn);
    box.appendChild(footer);

    // -- step 전환 --
    const optStepState = { count: props.optionCount };
    let optionListLoaded = false;
    const optionRefs: OptionStepRefs = {
        root: optionsRoot,
        countLabel: optionCountLabel ?? document.createElement("span"),
        state: optStepState,
    };

    function goToStep(name: "main" | "options"): void {
        steps.dataset.active = name;
        if (name === "options") {
            headTitle.textContent = "옵션 관리";
            backBtn.classList.remove("admin-modal__back--hidden");
            footer.classList.add("admin-modal__footer--hidden");
            if (!optionListLoaded) {
                optionListLoaded = true;
                void refreshOptionList(optionRefs, props.optionListUrl, props.optionReorderUrl);
            }
        } else {
            headTitle.innerHTML = `속성 편집<span class="admin-modal__title-sub">${props.fieldLabelKo}${props.fieldName ? ` · <code>${props.fieldName}</code>` : ""}</span>`;
            backBtn.classList.add("admin-modal__back--hidden");
            footer.classList.remove("admin-modal__footer--hidden");
        }
    }
    backBtn.addEventListener("click", () => goToStep("main"));

    // -- 저장 (메인 step 의 필드 속성만) --
    saveBtn.addEventListener("click", async () => {
        if (props.updateUrl === "") return;
        const fd = new FormData();
        mainStep.querySelectorAll<HTMLInputElement>("input[name]").forEach((inp) => {
            if (inp.type === "radio") {
                if (inp.checked) fd.set(inp.name, inp.value);
            } else if (inp.type === "checkbox") {
                fd.set(inp.name, inp.checked ? "1" : "0");
            } else {
                fd.set(inp.name, inp.value);
            }
        });

        // select_multi 가상 타입 → type=select + is_multiple=1 정규화
        const rawType = fd.get("type") as string | null;
        if (rawType === "select_multi") {
            fd.set("type", "select");
            fd.set("is_multiple", "1");
        }

        // 타입 변경 경고
        const newType = (rawType === "select_multi" ? "select" : rawType) ?? props.fieldType;
        const origType = (props.fieldType === "select_multi" ? "select" : props.fieldType);
        if (newType !== origType) {
            const ok = await Modal.confirm(
                "필드 타입을 변경하면 기존 게시글의 이 필드 값이 모두 삭제됩니다. 계속하시겠습니까?"
            );
            if (!ok) return;
        }

        const json = await postFormData(props.updateUrl, fd, "PATCH");
        if (json.result === "success") {
            Toast.success(json.msg ?? "저장했습니다.");
            const data = json.data as Record<string, unknown> | undefined;
            if (data) {
                const translations = data["translations"] as Record<string, { label?: string }> | undefined;
                const koLabel = translations?.["ko"]?.label ?? "";
                const enLabel = translations?.["en"]?.label ?? "";
                const labelKoEl = card.querySelector<HTMLElement>(".admin-boards-fields__label-ko");
                if (labelKoEl && koLabel) labelKoEl.textContent = koLabel;
                const labelEnEl = card.querySelector<HTMLElement>(".admin-boards-fields__label-en");
                if (labelEnEl) {
                    if (enLabel) {
                        labelEnEl.textContent = "/ " + enLabel;
                        labelEnEl.hidden = false;
                    } else {
                        labelEnEl.hidden = true;
                    }
                }
                const metaEl = card.querySelector<HTMLElement>(".admin-boards-fields__meta");
                const savedType = String(data["type"] ?? origType);
                const savedIsSelect = savedType === "select";
                if (metaEl) {
                    const isRequired = Boolean(data["is_required"]);
                    const isMultiple = Boolean(data["is_multiple"]);
                    const isFilterable = Boolean(data["is_filterable"]);
                    metaEl.innerHTML = [
                        isRequired ? '<span class="admin-badge admin-badge--danger">필수</span>' : "",
                        isMultiple && savedIsSelect ? '<span class="admin-badge admin-badge--info">다중</span>' : "",
                        isFilterable && savedIsSelect ? '<span class="admin-badge admin-badge--primary">필터</span>' : "",
                    ].join("");
                }
                // 타입 배지 업데이트
                const TYPE_LABELS: Record<string, string> = {};
                const TYPE_ICONS: Record<string, string> = {};
                props.typeOptions.forEach((opt) => {
                    if (opt.value !== "select_multi") {
                        TYPE_LABELS[opt.value] = opt.label;
                        TYPE_ICONS[opt.value] = opt.icon;
                    }
                });
                const typeBadgeEl = card.querySelector<HTMLElement>(".admin-boards-fields__type-badge");
                if (typeBadgeEl && savedType) {
                    const iconClass = TYPE_ICONS[savedType] ?? "";
                    const typeLabel = TYPE_LABELS[savedType] ?? savedType;
                    typeBadgeEl.innerHTML = iconClass
                        ? `<i class="${iconClass}" aria-hidden="true"></i> ${typeLabel}`
                        : typeLabel;
                }
                // data-field-type 속성 갱신
                card.dataset.fieldType = savedType;
                // 옵션 카운트 셀 업데이트 (select 아닌 경우 숨김)
                const optCountEl = card.querySelector<HTMLElement>(".admin-boards-fields__opt-count");
                const optDashEl = card.querySelector<HTMLElement>(".admin-table__muted");
                if (!savedIsSelect) {
                    if (optCountEl) optCountEl.textContent = "";
                    if (optDashEl) optDashEl.hidden = false;
                }
            }
            closeActiveModal();
        } else {
            Toast.error(json.msg ?? "저장 실패");
        }
    });

    // -- 옵션 step 내부 위임 (추가/편집/삭제) --
    optionsStep.addEventListener("submit", async (ev) => {
        const form = ev.target as HTMLFormElement;
        ev.preventDefault();
        if (form.matches("[data-option-new-form]")) {
            if (props.optionStoreUrl === "") return;
            const fd = new FormData(form);
            const json = await postFormData(props.optionStoreUrl, fd, "POST");
            if (json.result === "success") {
                Toast.success(json.msg ?? "옵션을 추가했습니다.");
                await refreshOptionList(optionRefs, props.optionListUrl, props.optionReorderUrl);
                updateCardOptionCount(card, optStepState.count);
            } else {
                Toast.error(json.msg ?? "옵션 추가 실패");
            }
            return;
        }
        if (form.matches("[data-option-edit-form]")) {
            const optCard = form.closest<HTMLElement>("[data-option-card]");
            const url = optCard?.dataset.updateUrl ?? "";
            if (url === "") return;
            const fd = new FormData(form);
            const json = await postFormData(url, fd, "PATCH");
            if (json.result === "success") {
                Toast.success(json.msg ?? "옵션을 저장했습니다.");
                await refreshOptionList(optionRefs, props.optionListUrl, props.optionReorderUrl);
                updateCardOptionCount(card, optStepState.count);
            } else {
                Toast.error(json.msg ?? "옵션 저장 실패");
            }
            return;
        }
    });

    optionsStep.addEventListener("click", async (ev) => {
        const target = ev.target as HTMLElement;
        if (target.closest("[data-option-edit-toggle]")) {
            const opt = target.closest<HTMLElement>("[data-option-card]");
            opt?.querySelector<HTMLElement>("[data-option-edit-form]")
                ?.classList.toggle("admin-boards-fields__option-edit--hidden");
            return;
        }
        if (target.closest("[data-option-edit-cancel]")) {
            const opt = target.closest<HTMLElement>("[data-option-card]");
            opt?.querySelector<HTMLElement>("[data-option-edit-form]")
                ?.classList.add("admin-boards-fields__option-edit--hidden");
            return;
        }
        if (target.closest("[data-option-new-cancel]")) {
            const det = target.closest<HTMLDetailsElement>("details");
            if (det) det.open = false;
            return;
        }
        if (target.closest("[data-option-destroy]")) {
            const optCard = target.closest<HTMLElement>("[data-option-card]");
            const ok = await destroyOption(optCard);
            if (ok) {
                await refreshOptionList(optionRefs, props.optionListUrl, props.optionReorderUrl);
                updateCardOptionCount(card, optStepState.count);
            }
            return;
        }
    });

    // body 직속 마운트
    document.body.appendChild(modal);
    activeModal = modal;

    // 모달 안 locale-tabs 핸들러 부착 (메인 step 의 탭)
    LocaleTabs.initAll(modal);

    // ESC 키 닫기 / 옵션 step 이면 뒤로
    escListener = (ev: KeyboardEvent) => {
        if (ev.key !== "Escape") return;
        if (steps.dataset.active === "options") {
            goToStep("main");
        } else {
            closeActiveModal();
        }
    };
    document.addEventListener("keydown", escListener);
}

/** 메인 페이지의 필드 행에 표시된 '옵션 N개' 카운트 동기화 */
function updateCardOptionCount(card: HTMLElement, count: number): void {
    const el = card.querySelector<HTMLElement>(".admin-boards-fields__opt-count");
    if (el) el.textContent = `${count}개`;
}
